import { cloneElement, createContext, useContext, type HTMLAttributes, type ReactElement } from 'react';
import { HiOutlineLockClosed } from 'react-icons/hi2';
import clsx from 'clsx';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import Tooltip from '../Tooltip';
import './ReadonlyIndicator.scss';

const message = 'Только для чтения';
const ReadonlyHintContext = createContext(false);

/** Слой подсказки — над окном, в котором поле */
const TOOLTIP_Z_INDEX = POPOVER_Z_INDEX;

/** Текст подсказки группы — одним элементом на модуль: подсказка висит на каждом недоступном поле формы */
const HINT_LABEL = (
    <span className="rf-readonly-tooltip__label">
        <HiOutlineLockClosed size={14} aria-hidden />
        {message}
    </span>
);

export const ReadonlyLock = ({ className }: { className?: string }) => (
    <Tooltip
        variant="compact"
        className="rf-readonly-tooltip"
        label={message}
        openDelay={200}
        zIndex={TOOLTIP_Z_INDEX}
        wrapTarget={false}
        interactive={false}
    >
        <span className={clsx('rf-readonly-lock', className)} role="img" aria-label={message}>
            <HiOutlineLockClosed size={14} aria-hidden />
        </span>
    </Tooltip>
);

/** Один фокусируемый триггер для недоступного контрола/группы, без дополнительной обёртки. */
export const ReadonlyHint = ({
    children,
    disabled = false,
}: {
    children: ReactElement<HTMLAttributes<HTMLElement>>;
    disabled?: boolean;
}) => {
    const parentHint = useContext(ReadonlyHintContext);
    if (disabled || parentHint) return children;
    return (
        <ReadonlyHintContext value={true}>
            <Tooltip
                variant="compact"
                className="rf-readonly-tooltip"
                label={HINT_LABEL}
                position="top"
                openDelay={200}
                zIndex={TOOLTIP_Z_INDEX}
                wrapTarget={false}
                interactive={false}
                // «Только для чтения» — постоянное описание группы (aria-description)
                describeTrigger={false}
            >
                {cloneElement(children, {
                    className: clsx(children.props.className, 'rf-readonly-hint'),
                    role: children.props.role ?? 'group',
                    tabIndex: 0,
                    'aria-description': message,
                })}
            </Tooltip>
        </ReadonlyHintContext>
    );
};
